import { yupResolver } from '@hookform/resolvers/yup'
import { Button, CircularProgress, DialogActions, FormControl, Grid, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'

// component import
import AccountDropdown from '../common/AccountDropdown'
import { Alert } from '../common/Alert'
import { CustomModal } from '../common/CustomModal'
import { CustomController } from '../common/CustomController'
import PaymentMethodController from '../common/PaymentMethodController'
import MatterDropdownWithForm from '../common/MatterDropdownWithForm'
import ClientDropdown from '../common/ClientDropdown'

// others
import { ALL_PAYMENT_METHODS, CANCEL } from '../../constants'
import { FetchAllAccountsQuery, useTransferFundsBetweenAccountsMutation } from '../../../generated'
import { transferFundsBetweenAccountsValidationSchema } from '../../validationSchema'
import { ErrorException } from '../../interfaceTypes'
import { ApolloError } from '@apollo/client'
import { TransferFundsBetweenAccountsFormValues } from '../../interfaceTypes/forms'
import { CustomDatePickerController } from '../common/CustomDatePickerRController'
import { formatCurrency } from '../../utils'
import { AccountOption } from '../../interfaceTypes/common'

interface TransferFundsProps {
  refetch?: () => void
  sourceAccount?: AccountOption<NonNullable<FetchAllAccountsQuery['fetchAllAccounts']['accounts']>[number]>
}

interface TransferFundsModalProps {
  isOpen: boolean
  refetch?: () => void
  sourceAccount?: AccountOption<NonNullable<FetchAllAccountsQuery['fetchAllAccounts']['accounts']>[number]>
  handleClose: () => void
}

const TransferFundsModal = ({
  isOpen,
  refetch,
  sourceAccount,
  handleClose: handleCloseModal
}: TransferFundsModalProps) => {
  const [accountBalance, setAccountBalance] = useState<string>('')

  const methods = useForm<TransferFundsBetweenAccountsFormValues>({
    mode: 'all',
    resolver: yupResolver(transferFundsBetweenAccountsValidationSchema),
    defaultValues: {
      sourceAccount: sourceAccount || null,
      destinationAccount: null,
      amount: '',
      date: new Date(),
      paymentMethod: null,
      referenceNumber: null,
      clientId: null,
      caseId: null,
      notes: null
    }
  })

  const {
    handleSubmit,
    control,
    formState: { errors: formErrors },
    reset,
    clearErrors
  } = methods

  const handleMutationError = (error: ApolloError) => {
    const { graphQLErrors } = error

    if (graphQLErrors) {
      graphQLErrors.map(
        // eslint-disable-next-line
        ({ extensions }) => {
          if (extensions) {
            const { exception } = extensions
            if (exception) {
              const { response } = exception as ErrorException
              const { response: errorResponse } = response || {}
              if (errorResponse) {
                const { error } = errorResponse
                Alert.error(error)
              } else {
                Alert.error('Something went wrong')
              }
            }
          }
        }
      )
    }
  }

  const [transferFundsBetweenAccountsMutation, { loading: transferFundsBetweenAccountsLoader }] =
    useTransferFundsBetweenAccountsMutation({
      onError: handleMutationError,
      onCompleted(data) {
        if (data?.transferFundsBetweenAccounts?.response?.status === 200) {
          Alert.success('Transaction completed successfully')
          handleClose()
          refetch?.()
        }
      }
    })

  const handleClose = () => {
    reset()
    clearErrors()
    setAccountBalance('')
    handleCloseModal()
  }

  const onSubmit: SubmitHandler<TransferFundsBetweenAccountsFormValues> = async (
    data: TransferFundsBetweenAccountsFormValues
  ) => {
    const { sourceAccount, destinationAccount, amount, date, paymentMethod, referenceNumber, clientId, caseId, notes } =
      data

    if (!sourceAccount?.value || !destinationAccount?.value || !paymentMethod) {
      Alert.error('Please fill all the fields')
      return
    }

    if (sourceAccount?.value === destinationAccount?.value) {
      Alert.error('Source and destination account cannot be the same')
      return
    }

    if (!sourceAccount?.data?.currentBalance || Number(sourceAccount?.data?.currentBalance) < Number(amount)) {
      Alert.error(`Insufficient balance in ${sourceAccount?.data?.name}`)
      return
    }

    transferFundsBetweenAccountsMutation({
      variables: {
        TransferFundsBetweenAccountsInput: {
          sourceAccountId: sourceAccount?.value,
          destinationAccountId: destinationAccount?.value,
          amount: amount || 0,
          date,
          paymentMethod: paymentMethod,
          referenceNumber,
          clientId,
          caseId,
          notes
        }
      }
    })
  }

  useEffect(() => {
    if (sourceAccount) {
      setAccountBalance(sourceAccount.data?.currentBalance?.toString() || '')
    }
  }, [sourceAccount])

  return (
    <CustomModal title={`Transfer Funds`} isOpen={isOpen} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl variant="outlined" fullWidth>
                <Controller
                  name="sourceAccount"
                  control={control}
                  render={({ field }) => (
                    <AccountDropdown
                      value={field.value}
                      onChange={field.onChange}
                      setBalance={setAccountBalance}
                      searchFilters={{
                        isBankAccount: true
                      }}
                      errorMessage={formErrors.sourceAccount?.value?.message || formErrors.sourceAccount?.message}
                      label="From Account"
                    />
                  )}
                />
              </FormControl>

              {accountBalance && (
                <Typography variant="subtitle1" color="text.secondary" pl={1} pt={0.5}>
                  Balance: {`${formatCurrency(Number(accountBalance))}`}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="destinationAccount"
                control={control}
                render={({ field }) => (
                  <AccountDropdown
                    value={field.value}
                    onChange={field.onChange}
                    searchFilters={{
                      isBankAccount: true
                    }}
                    errorMessage={
                      formErrors.destinationAccount?.value?.message || formErrors.destinationAccount?.message
                    }
                    label="To Account"
                  />
                )}
              />
            </Grid>

            <Grid item md={6} xs={12}>
              <CustomController
                controllerName="amount"
                controllerLabel="Amount"
                fieldType="number"
                dollarAdornment
                sx={{ my: 0 }}
              />
            </Grid>

            <Grid item md={6} xs={12}>
              <CustomDatePickerController controllerName="date" controllerLabel="Date" sx={{ my: 0 }} />
            </Grid>

            <Grid item md={6} xs={12}>
              <PaymentMethodController
                name="paymentMethod"
                label="Payment Method*"
                paymentMethods={ALL_PAYMENT_METHODS}
              />
            </Grid>

            <Grid item md={6} xs={12}>
              <CustomController controllerLabel="Reference No" controllerName="referenceNumber" sx={{ my: 0 }} />
            </Grid>

            <Grid item xs={12}>
              <MatterDropdownWithForm name="caseId" userId="" label="Matter" />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="clientId"
                control={control}
                render={({ field }) => (
                  <ClientDropdown
                    label={'Client'}
                    selectedClientId={field.value || undefined}
                    errorMessage={formErrors.clientId?.message}
                    onChange={option => field.onChange(option?.id || null)}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomController
                controllerName="notes"
                controllerLabel="Description"
                fieldType="textarea"
                isMultiLine
                rowsLength={4}
                sx={{ my: 0 }}
              />
            </Grid>
          </Grid>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose} aria-label={CANCEL}>
              {CANCEL}
            </Button>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={transferFundsBetweenAccountsLoader}
              aria-label="add new client"
              endIcon={transferFundsBetweenAccountsLoader && <CircularProgress size={20} color="inherit" />}
            >
              Transfer
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}

const TransferFunds = ({ sourceAccount, refetch }: TransferFundsProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleOpen = () => setIsOpen(true)

  const handleClose = () => {
    setIsOpen(false)
  }

  return (
    <>
      <Button color="primary" variant="outlined" onClick={handleOpen}>
        Transfer Funds
      </Button>

      {isOpen && (
        <TransferFundsModal isOpen={isOpen} refetch={refetch} sourceAccount={sourceAccount} handleClose={handleClose} />
      )}
    </>
  )
}

export default TransferFunds
